/**
 * Where the collaboration bar is pointed — the sidebar's address bar.
 *
 * A union with one member today. A second collaboration mode adds its own
 * variant carrying its own address shape, rather than another loose field on a
 * flat bag.
 */
export type CollaborationBarLocation = {
  mode: "ai-agent-chat";

  /** The thread on screen. ``null`` means a new, unsaved thread. It is
   *  ``null`` rather than ``undefined`` because the address is persisted, and
   *  ``undefined`` does not round-trip through JSON. */
  threadId: string | null;

  /** Which pane is showing. The history pane sits over the thread, so
   *  ``threadId`` stays meaningful while ``view`` is "history". */
  view: "thread" | "history";
};

/** The collaboration surfaces the bar knows how to show. */
export type CollaborationMode = CollaborationBarLocation["mode"];

export interface CollaborationBarState {
  /** Whether the bar is expanded. Orthogonal to where it points: a collapsed
   *  bar still remembers its location, the way a minimised window keeps its
   *  URL. Folding the two together — ``mode: null`` when closed — would
   *  destroy the address on close, and restoring it is the point. */
  isOpen: boolean;

  location: CollaborationBarLocation;
}

/** Navigation verbs, not setters. */
export type CollaborationBarAction =
  /** Expand the bar. Omit ``location`` to leave it pointed where it was; pass
   *  one to navigate there. */
  | { type: "open"; location?: CollaborationBarLocation }
  /** Collapse the bar. The location is remembered, not cleared. */
  | { type: "close" };

/** A bar that has never been opened points at a new chat thread. */
export const DEFAULT_COLLABORATION_BAR_STATE: CollaborationBarState = {
  isOpen: false,
  location: { mode: "ai-agent-chat", threadId: null, view: "thread" },
};

/**
 * Opening and navigating are one transition, so the bar can never be pointed
 * at a thread it is not showing. Opening without a location is the same
 * transition with the address left alone.
 */
export function collaborationBarReducer(
  state: CollaborationBarState,
  action: CollaborationBarAction,
): CollaborationBarState {
  switch (action.type) {
    case "open":
      return { isOpen: true, location: action.location ?? state.location };
    case "close":
      return { ...state, isOpen: false };
  }
}
